import { useCallback, useMemo, useState } from "react";
import type { Layout } from "react-grid-layout";
import { DEFAULT_LAYOUTS } from "@dashboard/components/DashboardWidgets/constants/layouts";
import { DEFAULT_ACTIVE_WIDGETS } from "@dashboard/components/DashboardWidgets/constants/dashboardConfig";
import {
    getColsForBreakpoint,
    getCurrentBreakpoint,
    isBreakpoint,
} from "@dashboard/utils/breakpoints";
import type {
    DashboardProfileConfig,
    ResponsiveLayouts,
    RGLLayoutItem,
} from "@dashboard/utils/dashboard.types";
import { findBestPosition } from "@dashboard/utils/layoutPlacement";
import { resolveWidgetDimensions } from "@dashboard/utils/widgetDimensions";
import { cloneDashboardProfileConfig } from "@dashboard/utils/dashboardProfileConfig";

export function useDashboardProfileDraft(initialConfig: DashboardProfileConfig, width: number) {
    const [draft, setDraft] = useState(() => cloneDashboardProfileConfig(initialConfig));
    const initialConfigJson = useMemo(() => JSON.stringify(initialConfig), [initialConfig]);
    const isDirty = JSON.stringify(draft) !== initialConfigJson;

    const setLayouts = useCallback((layouts: ResponsiveLayouts) => {
        setDraft((current) => ({ ...current, layouts }));
    }, []);

    const addWidget = useCallback((widgetId: string) => {
        setDraft((current) => ({
            ...current,
            activeWidgets: current.activeWidgets.includes(widgetId)
                ? current.activeWidgets
                : [...current.activeWidgets, widgetId],
        }));
    }, []);

    const removeWidget = useCallback((widgetId: string) => {
        setDraft((current) => ({
            ...current,
            activeWidgets: current.activeWidgets.filter((id) => id !== widgetId),
            layouts: Object.fromEntries(
                Object.entries(current.layouts).map(([breakpoint, layout]) => [
                    breakpoint,
                    layout?.filter((item: RGLLayoutItem) => item.i !== widgetId) ?? [],
                ]),
            ),
        }));
    }, []);

    const addWidgetToLayout = useCallback(
        (widgetId: string, position?: { x: number; y: number; w: number; h: number }) => {
            setDraft((current) => {
                const currentBreakpoint = getCurrentBreakpoint(width);
                const layouts: ResponsiveLayouts = {};

                Object.keys(current.layouts).forEach((breakpoint) => {
                    if (!isBreakpoint(breakpoint)) return;

                    const currentLayout = current.layouts[breakpoint] ?? [];
                    const dimensions = resolveWidgetDimensions(
                        widgetId,
                        getColsForBreakpoint(breakpoint),
                    );
                    const requestedPosition =
                        position && breakpoint === currentBreakpoint ? position : undefined;
                    const bestPosition = requestedPosition
                        ? requestedPosition
                        : findBestPosition(
                              Array.from(currentLayout),
                              dimensions.w,
                              dimensions.h,
                              breakpoint,
                          );

                    layouts[breakpoint] = [
                        ...Array.from(currentLayout),
                        {
                            i: widgetId,
                            x: bestPosition.x,
                            y: bestPosition.y,
                            w: requestedPosition?.w ?? dimensions.w,
                            h: requestedPosition?.h ?? dimensions.h,
                            minW: dimensions.minW,
                            minH: dimensions.minH,
                            maxH: dimensions.maxH,
                            resizeHandles: ["s", "w", "e", "n", "sw", "nw", "se", "ne"],
                        },
                    ];
                });

                return { ...current, layouts };
            });
        },
        [width],
    );

    const handleLayoutChange = useCallback(
        (_layout: Layout, allLayouts: ResponsiveLayouts) => {
            setLayouts(allLayouts);
        },
        [setLayouts],
    );

    const reset = useCallback(() => {
        setDraft({
            layouts: structuredClone(DEFAULT_LAYOUTS),
            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
        });
    }, []);

    const discardChanges = useCallback(() => {
        setDraft(cloneDashboardProfileConfig(initialConfig));
    }, [initialConfig]);

    return {
        draft,
        setLayouts,
        addWidget,
        removeWidget,
        addWidgetToLayout,
        handleLayoutChange,
        reset,
        discardChanges,
        isDirty,
    };
}
